import { getFeederTemplate, getFeederTemplateByLoadType } from './templates/feederTemplates';
import { getProjectSettings } from './settings/ProjectSettings';
import { generateUniqueId } from '../utils/generateUniqueId';
import { FEEDER_KINDS } from '../types/SimpleSupplyPoint';
import { CABLES, MCB, RCD } from '../electrical/Malzeme';

/**
 * Feeder Factory - Standart feeder'lar oluşturur
 * Template + Project Settings + Load Requirements kombinasyonu
 */
export class FeederFactory {

  /**
   * Yeni feeder oluşturur
   * @param {string} kind - Feeder türü (FEEDER_KINDS)
   * @param {string} fromComponentId - Kaynak component ID
   * @param {string} toComponentId - Hedef component ID
   * @param {object} overrides - Kullanıcı özel ayarları
   * @returns {object} Tam yapılandırılmış feeder
   */
  static createFeeder(kind, fromComponentId, toComponentId, overrides = {}) {

    // 1. Template'i al
    const template = getFeederTemplate(kind);
    if (!template) {
      throw new Error(`Unknown feeder kind: ${kind}`);
    }

    // 2. Project settings'i al
    const projectSettings = getProjectSettings();

    // 3. Project settings'e göre template'i uyarla (override'ları da gönder)
    const adaptedTemplate = this.applyProjectSettings(template, projectSettings, kind, overrides);
    // 4. Unique ID oluştur
    const feederId = overrides.id || generateUniqueId(`feeder_${kind}`);

    // 5. Tüm ayarları birleştir
    const feeder = {
      ...adaptedTemplate,
      ...overrides,
      id: feederId,
      kind,
      from: fromComponentId,
      to: toComponentId,
      sourceId: fromComponentId,
      targetId: toComponentId,

      // Cable ayarları derin birleştirme
      cable: {
        ...adaptedTemplate.cable,
        ...overrides.cable
      },

      // Electrical ayarları derin birleştirme
      electrical: {
        ...adaptedTemplate.electrical,
        ...overrides.electrical
      },

      // Protection ayarları derin birleştirme
      protection: {
        ...adaptedTemplate.protection,
        ...overrides.protection
      },

      // Metadata ayarları derin birleştirme
      metadata: {
        ...adaptedTemplate.metadata,
        ...overrides.metadata,
        createdAt: new Date().toISOString(),
        createdBy: 'FeederFactory'
      },

      // Vertices (kablo güzergahı) varsayılan
      vertices: overrides.vertices || []
    };

    // ✅ 6. Template'deki kuralları kullanarak eksik değerleri hesapla
    this.calculateDerivedValues(feeder);

    return feeder;
  }

  /**
   * Template kurallarına göre türetilen değerleri hesaplar
   */
  static calculateDerivedValues(feeder) {
    // Cable section'ı hesapla - minSection'dan başla
    if (feeder.cable.minSection && !feeder.cable.section) {
      feeder.cable.section = feeder.cable.minSection;
    }

    // Eğer section varsa ama minSection'dan küçükse, minSection'a yükselt
    if (feeder.cable.minSection && feeder.cable.section && feeder.cable.section < feeder.cable.minSection) {
      feeder.cable.section = feeder.cable.minSection;
    }

    // Voltage'ı hesapla (eğer yoksa phaseCount'a göre)
    if (!feeder.electrical.voltage && feeder.electrical.phaseCount) {
      feeder.electrical.voltage = feeder.electrical.phaseCount === 3 ? 380 : 220;
    }

    // ✅ sectionCount'u hesapla (iletken sayısı) - DAIMA yeniden hesapla
    if (feeder.electrical.phaseCount && feeder.cable.hasNeutral !== undefined && feeder.cable.hasGrounding !== undefined) {
      const calculatedSectionCount = this.calculateSectionCount(
        feeder.electrical.phaseCount,
        feeder.cable.hasNeutral,
        feeder.cable.hasGrounding
      );
      feeder.cable.sectionCount = calculatedSectionCount;
    }

    // coreCount (kablo sayısı) - default 1, paralel kablo durumunda artırılır
    if (!feeder.cable.coreCount) {
      feeder.cable.coreCount = 1;
    }

    // ✅ Cable indexNo seçimi - sectionCount ve section'a göre CABLES dizisinden seç
    if (!feeder.cable.indexNo && feeder.cable.section && feeder.cable.sectionCount && feeder.cable.type) {

      feeder.cable.indexNo = this.selectCableIndexNo(
        feeder.cable.section,
        feeder.cable.sectionCount,
        feeder.cable.type,
        feeder.cable.material || 'Cu'
      );

    }

    // Breaking capacity'yi ayarla
    if (feeder.protection?.mcb?.shortCircuitCurCap && !feeder.protection.mcb.shortCircuitCurCap) {
      feeder.protection.mcb.shortCircuitCurCap = feeder.protection.mcb.shortCircuitCurCap;
    }

    // ✅ MCB indexNo seçimi - phaseCount ve protection ayarlarına göre
    if (!feeder.protection?.mcb?.indexNo &&
      feeder.electrical.phaseCount &&
      feeder.protection?.mcb) {

      const minCurrent = feeder.protection.mcb.minCurrent || 25;
      const shortCircuitCurCap = feeder.protection.mcb.shortCircuitCurCap || 10;
      const curve = feeder.protection.mcb.curve || 'B';
      const type = feeder.protection.mcb.type || 'AOS';
      const neutralBreaking = feeder.protection.mcb.neutralBreaking !== undefined
        ? feeder.protection.mcb.neutralBreaking
        : false;


      feeder.protection.mcb.indexNo = this.selectMCBIndexNo(
        feeder.electrical.phaseCount,
        minCurrent,
        shortCircuitCurCap,
        curve,
        type,
        neutralBreaking
      );

      // ✅ MCB bulunduysa, current değerini MCB dizisinden al
      if (feeder.protection.mcb.indexNo !== null) {
        const selectedMCB = MCB[feeder.protection.mcb.indexNo];
        if (selectedMCB) {
          feeder.protection.mcb.current = selectedMCB.current;
        }
      }
    }

    // ✅ RCD current'ını MCB'den kopyala (MCB bulunduktan sonra)
    if (feeder.protection?.rcd && feeder.protection?.mcb?.current) {
      feeder.protection.rcd.current = feeder.protection.mcb.current;
    }

    // ✅ RCD sensitivity template'ten gelir (yoksa default 30mA)
    if (feeder.protection?.rcd && !feeder.protection.rcd.sensitivity) {
      feeder.protection.rcd.sensitivity = 30;
    }

    // ✅ RCD indexNo seçimi - phaseCount, current ve sensitivity'ye göre
    if (!feeder.protection?.rcd?.indexNo &&
      feeder.electrical.phaseCount &&
      feeder.protection?.rcd?.current &&
      feeder.protection?.rcd?.sensitivity) {

      const current = feeder.protection.rcd.current;
      const sensitivity = feeder.protection.rcd.sensitivity;

      feeder.protection.rcd.indexNo = this.selectRCDIndexNo(
        feeder.electrical.phaseCount,
        current,
        sensitivity
      );

      
    }
  }

  /**
   * Kablo özelliklerine göre CABLES dizisinden uygun indexNo'yu seçer
   * findCableByCurrent yapısını kullanır
   */
  static selectCableIndexNo(section, sectionCount, type, material = 'Cu') {
    // CABLES.find() kullanarak uygun kabloyu bul
    const cable = CABLES.find((obj) =>
      obj &&
      obj.section === section &&
      obj.sectionCount === sectionCount &&
      obj.type === type &&
      obj.material === material
    );

    if (cable) {
      // CABLES dizisindeki index'i bul
      const indexNo = CABLES.indexOf(cable);
      return indexNo;
    }

    return null;
  }

  /**
   * MCB özelliklerine göre MCB dizisinden uygun indexNo'yu seçer
   * @param {number} phaseCount - Faz sayısı (1 veya 3)
   * @param {number} minCurrent - Minimum akım (A)
   * @param {number} shortCircuitCurCap - Minimum kısa devre kesme kapasitesi (kA)
   * @param {string} curve - Kesici eğrisi ('B', 'C', 'D')
   * @param {string} type - Kesici tipi ('AOS')
   * @param {boolean} neutralBreaking - Nötr kesme özelliği (true: nötr keser, false: nötr kesmez)
   */
  static selectMCBIndexNo(phaseCount, minCurrent = 25, shortCircuitCurCap = 10, curve = 'B', type = 'AOS', neutralBreaking = false) {
    // ✅ Polar hesaplama:
    // neutralBreaking = false (main feeder): 1F→1P, 3F→3P
    // neutralBreaking = true (feeder'lar): 1F→2P (1P+N), 3F→4P (3P+N)
    const polar = neutralBreaking ? (phaseCount === 1 ? 2 : 4) : phaseCount;

    // MCB.find() ile uygun kesiciyi bul
    const mcb = MCB.find((obj) =>
      obj &&
      obj.polar === polar &&
      obj.current >= minCurrent &&
      obj.shortCircuitCurCap >= shortCircuitCurCap &&
      obj.curve === curve &&
      obj.type === type
    );

    if (mcb) {
      const indexNo = MCB.indexOf(mcb);
      return indexNo;
    }

    return null;
  }

  /**
   * RCD özelliklerine göre RCD dizisinden uygun indexNo'yu seçer
   * @param {number} phaseCount - Faz sayısı (1 veya 3)
   * @param {number} current - RCD akımı (A) - MCB current ile aynı
   * @param {number} sensitivity - Hassasiyet (mA): 30 veya 300
   */
  static selectRCDIndexNo(phaseCount, current = 25, sensitivity = 30) {
    // RCD için polar hesaplama: 1F → 2P, 3F → 4P (RCD'ler nötr dahil)
    const polar = phaseCount === 1 ? 2 : 4;

    // Tam eşleşme ile RCD bul (current ve sensitivity tam eşleşmeli)
    const rcd = RCD.find((obj) =>
      obj &&
      obj.polar === polar &&
      obj.current === current &&
      obj.sensitivity === sensitivity
    );

    if (rcd) {
      const indexNo = RCD.indexOf(rcd);
      return indexNo;
    }

    return null;
  }

  /**
   * Load tipine göre uygun feeder oluşturur
   * @param {string} loadType - socket, lighting, machine, panel
   * @param {number} power - Yük gücü (W)
   * @param {string} fromComponentId - Kaynak component ID
   * @param {string} toComponentId - Hedef component ID
   * @param {object} overrides - Kullanıcı özel ayarları
   */
  static createFeederForLoad(loadType, power, fromComponentId, toComponentId, overrides = {}) {
    if (!['socket', 'lighting', 'lightSwitch', 'lightFixture', 'machine', 'panel', 'bell'].includes(loadType)) {
      throw new Error(`Invalid load type: ${loadType}`);
    }
    // Load tipine göre uygun template seç
    const template = getFeederTemplateByLoadType(loadType, power);
    if (!template) {
      throw new Error(`Cannot determine feeder type for load: ${loadType}`);
    }

    // Güce göre kablo kesitini ve koruma cihazlarını ayarla
    const adjustedTemplate = this.adjustForPower(template, power, loadType);

    // Feeder kind'ını belirle
    const feederKind = this.getFeederKindByLoadType(loadType);

    return this.createFeeder(feederKind, fromComponentId, toComponentId, {
      ...adjustedTemplate,
      ...overrides
    });
  }

  /**
   * Panel arası bağlantı feeder'ı oluşturur
   */
  static createPanelFeeder(fromPanelId, toPanelId, overrides = {}) {
    return this.createFeeder(FEEDER_KINDS.PANEL_FEEDER, fromPanelId, toPanelId, overrides);
  }

  /**
   * Ana besleyici feeder oluşturur
   */
  static createMainFeeder(fromSupplyId, toPanelId, overrides = {}) {
    return this.createFeeder(FEEDER_KINDS.MAIN_FEEDER, fromSupplyId, toPanelId, overrides);
  }

  /**
   * Faz sayısına göre kablo kesit sayısını hesaplar
   */
  static calculateSectionCount(phaseCount, hasNeutral, hasGrounding) {
    let count = 0;

    if (phaseCount === 3) {
      count = 3; // 3 Faz (L1, L2, L3)
    } else {
      count = 1; // Tek Faz (L)
    }

    if (hasNeutral) count += 1;     // Nötr (N)
    if (hasGrounding) count += 1;   // Toprak (PE)

    return count;
  }

  /**
   * Akıma göre uygun kesici akımını seçer
   */
  static selectMCBCurrent(calculatedCurrent, minCurrent = 0) {
    const standardCurrents = [6, 10, 13, 16, 20, 25, 32, 40, 50, 63, 80, 100, 125];

    // Hesaplanan akım * 1.25 güvenlik faktörü
    const requiredCurrent = Math.max(calculatedCurrent * 1.25, minCurrent);

    // Standart akımlardan uygun olanı seç
    return standardCurrents.find(i => i >= requiredCurrent) || standardCurrents[standardCurrents.length - 1];
  }

  /**
   * Project settings'e göre template'i uyarlar
   */
  static applyProjectSettings(template, projectSettings, kind, overrides = {}) {
    const adapted = JSON.parse(JSON.stringify(template)); // Deep copy

    // Feeder-specific settings'i uygula (electrical ve cable özellikleri)
    if (projectSettings.feeders) {
      const feederSettings = this.getFeederSettingsForKind(kind, projectSettings.feeders);
      if (feederSettings) {
        // ⚠️ Cable indexNo kaldırıldı - calculateDerivedValues'te hesaplanacak

        // ✅ Electrical settings - Override'daki değerleri öncelikli kullan
        const finalPhaseCount = overrides.electrical?.phaseCount ?? feederSettings.phaseCount ?? adapted.electrical.phaseCount;

        if (feederSettings.voltage) {
          // Faz sayısına göre voltage ayarla
          adapted.electrical.voltage = finalPhaseCount === 3 ? 380 : 220;
        }
        if (feederSettings.phaseCount !== undefined) {
          adapted.electrical.phaseCount = finalPhaseCount;
        }

        // Protection settings
        if (feederSettings.protection?.mcb) {
          if (!adapted.protection) adapted.protection = {};
          if (!adapted.protection.mcb) adapted.protection.mcb = {};

          adapted.protection.mcb = {
            ...adapted.protection.mcb,
            ...feederSettings.protection.mcb
          };
        }

        if (feederSettings.protection?.rcd) {
          if (!adapted.protection) adapted.protection = {};
          if (!adapted.protection.rcd) adapted.protection.rcd = {};

          adapted.protection.rcd = {
            ...adapted.protection.rcd,
            ...feederSettings.protection.rcd
          };
        }
      }
    }

    // Cable ayarları
    if (adapted.cable) {
      // Varsayılan kablo tipini proje ayarlarından al
      const cableSettings = projectSettings.cables;
      if (cableSettings?.defaultTypes) {
        // Installation tipine göre kablo tipi seç
        switch (adapted.metadata?.installationType) {
          case 'underground':
            adapted.cable.type = cableSettings.defaultTypes.underground;
            break;
          case 'conduit':
          case 'cable_tray':
            adapted.cable.type = cableSettings.defaultTypes.indoor;
            break;
        }
      }

      // Varsayılan malzeme
      if (cableSettings?.defaultMaterial) {
        adapted.cable.material = cableSettings.defaultMaterial;
      }

      // Minimum kesit kontrolü - template'teki minSection'ı güncelle
      if (cableSettings?.minimumSections) {
        const projectMinSection = this.getMinimumSectionForKind(kind, cableSettings.minimumSections);
        if (projectMinSection) {
          // Template'teki minSection'ı project settings'teki ile karşılaştır
          if (!adapted.cable.minSection || adapted.cable.minSection < projectMinSection) {
            adapted.cable.minSection = projectMinSection;
          }
        }
      }
    }

    // Protection ayarları - SADECE eksik olanları doldur, mevcut değerleri ezme!
    if (adapted.protection && projectSettings.cables?.protection) {
      const protectionSettings = projectSettings.cables.protection;

      if (adapted.protection.mcb) {
        if (!adapted.protection.mcb.curve) {
          adapted.protection.mcb.curve = protectionSettings.defaultMCBCurve;
        }
      }

      if (adapted.protection.rcd) {
        if (!adapted.protection.rcd.type) {
          adapted.protection.rcd.type = protectionSettings.defaultRCDType;
        }
        // ✅ Sensitivity sadece yoksa default değeri kullan (template/feeder-specific değeri ezme!)
        if (!adapted.protection.rcd.sensitivity) {
          adapted.protection.rcd.sensitivity = protectionSettings.defaultRCDSensitivity;
        }
      }
    }

    return adapted;
  }

  /**
   * Güce göre kablo kesitini ve koruma ayarlarını ayarlar
   */
  static adjustForPower(template, power, loadType) {
    const adjusted = JSON.parse(JSON.stringify(template));

    // Güce göre akım hesapla
    const voltage = adjusted.electrical.voltage;
    const phaseCount = adjusted.electrical.phaseCount;
    const powerFactor = 0.8; // Varsayılan

    let current;
    if (phaseCount === 3) {
      current = power / (Math.sqrt(3) * voltage * powerFactor);
    } else {
      current = power / 220 // (voltage * powerFactor);
    }

    // Güvenlik faktörü ekle
    current *= 1.25;

    // Kablo kesitini ayarla
    if (current > 20) {
      adjusted.cable.section = 4;
      adjusted.electrical.current = 25;
      adjusted.electrical.maxCurrent = 32;
    } else if (current > 16) {
      adjusted.cable.section = 2.5;
      adjusted.electrical.current = 20;
      adjusted.electrical.maxCurrent = 25;
    } else if (current > 10) {
      adjusted.cable.section = 2.5;
      adjusted.electrical.current = 16;
      adjusted.electrical.maxCurrent = 20;
    }

    // MCB değerini ayarla
    if (adjusted.protection?.mcb) {
      adjusted.protection.mcb.current = adjusted.electrical.current;
    }

    // Makine için özel ayarlar
    if (loadType === 'machine') {
      // Makine için B eğrisi tercih edilir
      if (adjusted.protection?.mcb) {
        adjusted.protection.mcb.curve = 'B';
      }

      // Yüksek güçlü makineler için 3 faz
      if (power > 5000) {
        adjusted.electrical.phaseCount = 3;
        adjusted.electrical.voltage = 380;
        adjusted.cable.sectionCount = 4; // 3L+PE
      }
    }

    return adjusted;
  }

  /**
   * Load tipine göre feeder kind'ını belirler
   */
  static getFeederKindByLoadType(loadType) {
    switch (loadType) {
      case 'socket':
        return FEEDER_KINDS.SOCKET_CIRCUIT;
      case 'lighting':
      case 'lightSwitch':
      case 'lightFixture':
        return FEEDER_KINDS.LIGHTING_CIRCUIT;
      case 'machine':
        return FEEDER_KINDS.MACHINE_CIRCUIT;
      case 'panel':
        return FEEDER_KINDS.PANEL_FEEDER;
      case 'bell':
        return FEEDER_KINDS.LOWCURRENT_CIRCUIT; // Bell için lowcurrent circuit
      default:
        return FEEDER_KINDS.BRANCH_CIRCUIT;
    }
  }

  /**
   * Feeder kind'ına göre project settings'ten uygun feeder ayarlarını döndürür
   */
  static getFeederSettingsForKind(kind, feederSettings) {
    switch (kind) {
      case FEEDER_KINDS.MAIN_FEEDER:
        return feederSettings.mainFeeder;
      case FEEDER_KINDS.DIST_FEEDER:
        return feederSettings.distFeeder;
      case FEEDER_KINDS.PANEL_FEEDER:
      case FEEDER_KINDS.SUB_PANEL_FEEDER:
        return feederSettings.panelFeeder;
      case FEEDER_KINDS.SOCKET_CIRCUIT:
        return feederSettings.branchCircuits?.socketCircuit;
      case FEEDER_KINDS.LIGHTING_CIRCUIT:
        return feederSettings.branchCircuits?.lightingCircuit;
      case FEEDER_KINDS.MACHINE_CIRCUIT:
        return feederSettings.branchCircuits?.machineCircuit;
      case FEEDER_KINDS.BRANCH_CIRCUIT:
        // Generic branch circuit - default to socket circuit
        return feederSettings.branchCircuits?.socketCircuit;
      default:
        return null;
    }
  }

  /**
   * Feeder kind'ına göre minimum kablo kesitini döndürür
   */
  static getMinimumSectionForKind(kind, minimumSections) {
    const kindToSectionMap = {
      [FEEDER_KINDS.LIGHTING_CIRCUIT]: minimumSections.lighting,
      [FEEDER_KINDS.SOCKET_CIRCUIT]: minimumSections.socket,
      [FEEDER_KINDS.MACHINE_CIRCUIT]: minimumSections.machine,
      [FEEDER_KINDS.PANEL_FEEDER]: minimumSections.panel,
      [FEEDER_KINDS.MAIN_FEEDER]: minimumSections.main,
      [FEEDER_KINDS.BRANCH_CIRCUIT]: minimumSections.socket
    };

    return kindToSectionMap[kind];
  }

  /**
   * Feeder'ı validate eder ve eksik alanları tamamlar
   */
  static validateFeeder(feeder) {
    const errors = [];
    const warnings = [];

    // Zorunlu alanları kontrol et
    if (!feeder.id) errors.push('Feeder ID is required');
    if (!feeder.from) errors.push('From component ID is required');
    if (!feeder.to) errors.push('To component ID is required');

    // Cable kontrolü
    if (!feeder.cable) {
      errors.push('Cable configuration is required');
    } else {
      if (feeder.cable.section <= 0) {
        errors.push('Invalid cable section');
      }
      if (feeder.cable.sectionCount <= 0) {
        errors.push('Invalid cable section count');
      }
    }

    // Electrical kontrolü
    if (!feeder.electrical) {
      errors.push('Electrical configuration is required');
    } else {
      if (feeder.electrical.voltage <= 0) {
        warnings.push('Invalid voltage value');
      }
      if (feeder.electrical.current <= 0) {
        warnings.push('Invalid current value');
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }
}

// Convenience methods
export const createSocketFeeder = (power, fromId, toId, overrides) =>
  FeederFactory.createFeederForLoad('socket', power, fromId, toId, overrides);

export const createMachineFeeder = (power, fromId, toId, overrides) =>
  FeederFactory.createFeederForLoad('machine', power, fromId, toId, overrides);

export const createLightingFeeder = (power, fromId, toId, overrides) =>
  FeederFactory.createFeederForLoad('lighting', power, fromId, toId, overrides);

export const createPanelFeeder = (fromId, toId, overrides) =>
  FeederFactory.createPanelFeeder(fromId, toId, overrides);

export const createMainFeeder = (fromId, toId, overrides) =>
  FeederFactory.createMainFeeder(fromId, toId, overrides);